import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Drawer, Button } from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LeftOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Cookies from "js-cookie";
import httpInjectorService from "../services/http-injector.service";
// This image already has the logo and graphics baked in — we only
// place the heading/subtext/cards over its blank middle area
import heroImage from "../assets/images/Peace_payroll_no_gemini.png";
import { AuthContext } from "../AuthContext";
import { NotificationContext } from "../NotificationProvider";
import Forgotpassword from "../components/auth/Forgotpassword";

const FEATURES = [
  { icon: "🕐", title: "Same Day", desc: "Accurate payments on time" },
  { icon: "📄", title: "Compliant", desc: "Full regulatory adherence" },
  { icon: "👥", title: "Integrity", desc: "Systematic values & procedures" },
];

const SignIn = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { openNotification } = useContext(NotificationContext);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const toggleVisibility = () => setVisible(!visible);

  const handleForgotPassword = () => setIsDrawerVisible(true);

  const handleBack = () => {
    navigate("/sign-in");
    setIsDrawerVisible(false);
  };

  const onFinish = async (values) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await httpInjectorService.adminlogin(values);
      if (response.status === "SUCCESS") {
        openNotification("success", response.message);
        Cookies.set("authUser", response.data.tokenid);
        Cookies.set("email", values.email);
        Cookies.set("role_id", response.data.role_id);
        Cookies.set("user_id", response.data.user_id);
        Cookies.set("username", response.data.username);
        login(response.data.tokenid);
        navigate("/dashboard");
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div style={styles.page}>
      {/* Sign-in content — blurs while the forgot-password drawer is open.
          Drawer itself is unaffected since antd portals it to document.body. */}
      <div
        style={{
          ...styles.pageContent,
          filter: isDrawerVisible ? "blur(6px)" : "none",
          pointerEvents: isDrawerVisible ? "none" : "auto",
        }}
        aria-hidden={isDrawerVisible}
      >
        {/* LEFT SIDE — your image, content placed over its blank space */}
        <div style={styles.leftPane}>
          <img src={heroImage} alt="" style={styles.heroImage} />

          <div style={styles.leftContent}>
            <h1 style={styles.heroHeading}>
              Welcome back to
              <br />
              PeacePayroll Admin
            </h1>

            <p style={styles.heroSubtext}>
              Manage your entire team, upload paysheets securely, and access
              all employee details from one centralized hub.
            </p>

            <div style={styles.featureRow}>
              {FEATURES.map((f, i) => (
                <div style={styles.featureCard} key={i}>
                  <div style={styles.featureIcon}>{f.icon}</div>
                  <div style={styles.featureTitle}>{f.title}</div>
                  <div style={styles.featureDesc}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE — login form */}
        <div style={styles.rightPane}>
          {/* <div style={styles.phoneRow}>
            <PhoneOutlined />
            <span style={{ marginLeft: 8 }}>020 4505 4951</span>
          </div> */}

          <div style={styles.loginCard}>
            <h2 style={styles.cardTitle}>ADMIN PORTAL LOGIN</h2>

            <Form
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                label={
                  <span>
                    <span style={{ color: "#e04747" }}>* </span>Email address
                  </span>
                }
                name="email"
                rules={[
                  { required: true, message: "Please input your email!" },
                  {
                    pattern:
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter a valid email address!",
                  },
                ]}
              >
                <Input
                  placeholder="Email"
                  size="large"
                  style={styles.input}
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    <span style={{ color: "#e04747" }}>* </span>Password
                  </span>
                }
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                  {
                    pattern:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/,
                    message: (
                      <span>
                        Password must contain at least one uppercase letter,
                        one lowercase letter,
                        <br />
                        and one special character, and be at least 6
                        characters long!
                      </span>
                    ),
                  },
                ]}
              >
                <Input
                  type={visible ? "text" : "password"}
                  placeholder="Password"
                  size="large"
                  style={styles.input}
                  disabled={loading}
                  suffix={
                    visible ? (
                      <EyeOutlined onClick={toggleVisibility} />
                    ) : (
                      <EyeInvisibleOutlined onClick={toggleVisibility} />
                    )
                  }
                />
              </Form.Item>

              <div style={{ marginBottom: 20 }}>
                <span style={styles.forgotLink} onClick={handleForgotPassword}>
                  Forgot Password?
                </span>
              </div>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  style={styles.submitBtn}
                >
                  {loading ? (
                    <>
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                      <span>SIGNING IN...</span>
                    </>
                  ) : (
                    <>
                      <span>SIGN IN</span>
                      <span style={styles.submitArrow}>
                        <ArrowRightOutlined />
                      </span>
                    </>
                  )}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>

      <Drawer
        placement="left"
        width={800}
        onClose={() => setIsDrawerVisible(false)}
        visible={isDrawerVisible}
        destroyOnClose={true}
        mask={false}
        closable={false}
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined
              style={{ fontSize: 20, cursor: "pointer", marginRight: 8 }}
              onClick={handleBack}
            />
            <span>Back To Login</span>
          </div>
        }
      >
        <Forgotpassword />
      </Drawer>
    </div>
  );
};

const styles = {
  page: {
    display: "flex",
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
  },
  pageContent: {
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "filter 0.25s ease",
  },
  leftPane: {
    position: "relative",
    width: "58%",
    height: "100%",
    overflow: "hidden",
  },
  heroImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  leftContent: {
    position: "absolute",
    top: "32%",
    left: "20%",
    width: "60%",
    zIndex: 2,
  },
  heroHeading: {
    fontSize: "34px",
    fontWeight: 800,
    lineHeight: 1.15,
    color: "#14142b",
    margin: "0 0 14px 0",
  },
  heroSubtext: {
    fontSize: "14px",
    color: "#4a4a5a",
    lineHeight: 1.6,
    maxWidth: "440px",
    marginBottom: "22px",
  },
  featureRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "nowrap",
  },
  featureCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "12px 10px",
    flex: "1 1 0",
    minWidth: 0,
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  },
  featureIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "#fdeee0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    marginBottom: "8px",
  },
  featureTitle: { fontWeight: 700, fontSize: "13px", marginBottom: "3px" },
  featureDesc: { fontSize: "11px", color: "#8a8a99" },

  rightPane: {
    width: "42%",
    height: "100%",
    background: "#faf3e8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    padding: "40px",
  },
  phoneRow: {
    position: "absolute",
    top: "32px",
    right: "48px",
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#333",
  },
  loginCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "44px 40px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    marginBottom: "28px",
    color: "#14142b",
  },
  input: {
    borderRadius: "8px",
    padding: "10px 12px",
  },
  forgotLink: {
    color: "#7b3fe4",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: "14px",
  },
  submitBtn: {
    width: "100%",
    height: "48px",
    borderRadius: "999px",
    background: "#1a1f6e",
    borderColor: "#1a1f6e",
    fontWeight: 600,
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  submitArrow: {
    background: "#f5820d",
    borderRadius: "50%",
    width: "22px",
    height: "22px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "12px",
  },
};

export default SignIn;
